
import React, { useEffect, useRef, useState } from 'react';

interface ElfsightWidgetProps {
  constrainMobile?: boolean;
}

const ElfsightWidget = ({ constrainMobile = false }: ElfsightWidgetProps) => {
  const widgetId = "506d0367-bacf-44b0-bd6a-deef764060ce";
  const containerRef = useRef<HTMLDivElement>(null);
  const initAttempted = useRef(false);
  const [fallback, setFallback] = useState(false);
  
  useEffect(() => {
    // Prevent multiple initialization attempts
    if (initAttempted.current) return;
    initAttempted.current = true;
    
    // Check if widget is already initialized with iframe
    const existingWidget = document.querySelector(`.elfsight-app-${widgetId} iframe`);
    if (existingWidget) {
      console.log("Elfsight logo widget already exists with iframe");
      return;
    }
    
    const initializeWidget = () => {
      // Ensure container is visible and has height
      const container = containerRef.current;
      if (container) {
        container.style.display = 'block';
        container.style.minHeight = '80px';
      }

      // Try with ELF global object
      const elfsightPlatform = (window as any).ELF || (window as any).ElfSight;
      if (elfsightPlatform && typeof elfsightPlatform.initialize === 'function') {
        try {
          elfsightPlatform.initialize();
          (window as any).elfsightWidgetsLoaded = true;
        } catch (e) {
          console.error("Error initializing Elfsight platform:", e);
        }
      }
      
      // Also try with reinitializeElfsightWidgets global function
      if ((window as any).reinitializeElfsightWidgets) {
        setTimeout((window as any).reinitializeElfsightWidgets, 100);
      }
    };
    
    // Initialization with retries to handle slow mobile browsers
    const attemptInit = () => {
      setTimeout(initializeWidget, 200);
      setTimeout(initializeWidget, 800);
      setTimeout(initializeWidget, 2000);
    };
    
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      attemptInit();
    } else {
      window.addEventListener('DOMContentLoaded', attemptInit);
    }
    
    // Initialize on window load, tab focus, and visibility changes (helps especially on mobile)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') attemptInit();
    };
    window.addEventListener('load', attemptInit);
    window.addEventListener('focus', attemptInit);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('DOMContentLoaded', attemptInit);
      window.removeEventListener('load', attemptInit);
      window.removeEventListener('focus', attemptInit);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [widgetId]);

  // Ensure iframe gets proper height on mobile
  useEffect(() => {
    const ensureHeight = () => {
      const container = containerRef.current;
      const iframe = container?.querySelector('iframe') as HTMLIFrameElement | null;
      if (iframe) {
        const shouldScale = constrainMobile && window.matchMedia('(max-width: 767px)').matches;
        iframe.style.width = shouldScale ? '125%' : '100%';
        iframe.style.maxWidth = shouldScale ? 'none' : '100%';
        iframe.style.minHeight = shouldScale ? '80px' : '80px';
        iframe.style.height = shouldScale ? '80px' : '96px';
        iframe.style.display = 'block';
        iframe.style.boxSizing = 'border-box';
        iframe.style.transform = shouldScale ? 'scale(0.8)' : 'none';
        iframe.style.transformOrigin = 'top left';
        return true;
      }
      return false;
    };

    // Try immediately and on interval - keep trying longer for mobile
    const interval = setInterval(ensureHeight, 150);

    // Observe DOM changes inside the container
    const observer = new MutationObserver(() => ensureHeight());
    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, subtree: true });
    }

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [constrainMobile]);


  return (
    <div
      className={`w-full max-w-full py-0 elfsight-wrapper ${
        constrainMobile ? 'overflow-hidden sm:overflow-visible' : 'overflow-visible'
      }`}
      style={{ marginTop: '-1px' }}
    >
      {/* Container for the logo showcase widget */}
      <div className="h-[64px] sm:h-[80px] lg:h-[100px] w-full max-w-full overflow-hidden sm:overflow-visible" style={{ minHeight: '64px' }}>
        <div 
          ref={containerRef}
          className={`elfsight-app-${widgetId} w-full h-full`}
          data-elfsight-app-lazy
        />
      </div>
    </div>
  );
};

export default ElfsightWidget;
